// @ts-strict-ignore
import { AsyncPipe, DOCUMENT, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingLabel } from 'src/app/core/services/tracking/models/tracking-label.enum';
import { TrackingInput } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { hasChanged } from 'src/app/utils/functions/change-helpers';

import { LazyHostComponent } from '../../core/lazy-host/lazy-host.component';
import { TrackClickOnceDirective } from '../../core/services/tracking/directive/track-click-once.directive';
import { SelectablesSearchService } from '../selectables-search/selectables-search.service';

@Component({
  selector: 'cc-selectables-search-host',
  templateUrl: './selectables-search-host.component.html',
  styleUrl: './selectables-search-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, TrackClickOnceDirective, LazyHostComponent, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SelectablesSearchHostComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isMQ3max!: boolean;

  public isInitializeSelectablesSearch = false;
  public isDisplaySelectablesSearch = false;
  public readonly unsubscribeKeybindingsSubject: Subject<void> = new Subject<void>();
  public readonly selectablesSearchComponent = LazyComponentId.SelectablesSearch;
  public readonly isSearchEnabledByMarket$: Observable<boolean> = this.store.select(
    fromCarl.selectConfig<boolean>('selectablesSearch.enabled')
  );
  public readonly trackingData: TrackingInput = {
    clickData: {
      category: TrackingCategory.Search,
      label: TrackingLabel.SearchOpen,
      action: TrackingAction.ConfiguratorSearch
    }
  };

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store,
    private readonly selectablesSearchService: SelectablesSearchService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnChanges({ isMQ3max }: SimpleChanges): void {
    if (hasChanged(isMQ3max)) {
      this.selectablesSearchService.setSelectablesSearchVisibility(!!isMQ3max.currentValue);
    }
  }

  public ngOnInit(): void {
    this.registerKeyboardShortcuts();
    this.subscribeToVisibilityChange();
  }

  public ngOnDestroy(): void {
    this.unsubscribeKeybindingsSubject.next();
  }

  public toggleSelectablesSearch(clickEvent?: MouseEvent): void {
    // clicking outside of the popup normally closes it. this must not happen if we click the search button which is supposed to open it.
    // we therefore prevent the click event from reaching it.
    clickEvent?.stopPropagation();

    // this starts lazy loading and only needs to be set to true once.
    this.isInitializeSelectablesSearch = true;

    // show / hide the DOM element without removing it (otherwise each visibility toggle will lazy load the whole module again)
    this.isDisplaySelectablesSearch = !this.isDisplaySelectablesSearch;
    this.selectablesSearchService.setSelectablesSearchVisibility(this.isDisplaySelectablesSearch);

    //hide the popup with escape key
    if (this.isDisplaySelectablesSearch) {
      fromEvent(this.document, 'keyup')
        .pipe(
          filter((event: KeyboardEvent) => event.key === 'Escape'),
          takeUntil(this.unsubscribeKeybindingsSubject)
        )
        .subscribe(() => {
          this.selectablesSearchService.setSelectablesSearchVisibility(false);
          this.changeDetectorRef.markForCheck();
        });
    } else {
      this.unsubscribeKeybindingsSubject.next();
    }

    this.changeDetectorRef.markForCheck();
  }

  private registerShortcut(shortcuts: string[]): Observable<KeyboardEvent[]> {
    // Observables for all keydown and keyup events
    const keyDown$ = fromEvent<KeyboardEvent>(this.document, 'keydown');
    const keyUp$ = fromEvent<KeyboardEvent>(this.document, 'keyup');

    // All KeyboardEvents - emitted only when KeyboardEvent changes (key or type)
    const keyEvents$ = merge(keyDown$, keyUp$).pipe(
      distinctUntilChanged(
        (keyDownEvent, keyUpEvent) => keyDownEvent.code === keyUpEvent.code && keyDownEvent.type === keyUpEvent.type
      )
    );

    const createKeyPressStream = (charCode: string): Observable<KeyboardEvent> =>
      keyEvents$.pipe(filter((event: KeyboardEvent) => event.code === charCode.valueOf()));

    // Create Event Stream for every KeyCode in shortcut
    const keyCodeEvents$ = shortcuts.map(shortcut => createKeyPressStream(shortcut));

    // Emit when specified keys are pressed (keydown).
    // Emit only when all specified keys are pressed at the same time.
    return combineLatest(keyCodeEvents$).pipe(
      filter<KeyboardEvent[]>(events => events.every(event => event.type === 'keydown'))
    );
  }

  private subscribeToVisibilityChange(): void {
    this.selectablesSearchService.selectablesSearchVisibility$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(visible => {
        this.isDisplaySelectablesSearch = visible;
      });
  }

  private registerKeyboardShortcuts(): void {
    this.registerShortcut(['ControlLeft', 'Space'])
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(() => {
        this.toggleSelectablesSearch();
      });
  }
}
